import { apiService } from '@clarix/api-client';
import { pmsBase } from '../lib/base';
import { PMS_ENDPOINTS } from '../endpoints';
import type {
  AssignUserRolePayload,
  CreateRoleTemplatePayload,
  MyPmsAccess,
  PmsRoleTemplate,
  PmsUserRoleRow,
  UpdateRoleTemplatePayload,
} from '../types';

/**
 * PMS RBAC client (M8).
 *
 * `getMyAccess` is the hydration source for the shell's permission gating. It is
 * ungated server-side on purpose: gating "what may I do?" behind a permission is
 * circular, and a client that cannot answer it has no way to tell "not allowed"
 * from "not loaded".
 */
export const rbacApi = {
  getMyAccess: () => apiService.get<MyPmsAccess>(`${pmsBase()}${PMS_ENDPOINTS.ME_PERMISSIONS}`),

  listRoles: () => apiService.get<PmsRoleTemplate[]>(`${pmsBase()}${PMS_ENDPOINTS.SETTINGS_ROLES}`),

  /** Create a custom role template; returns the full, re-ordered list. */
  createRole: (data: CreateRoleTemplatePayload) =>
    apiService.post<PmsRoleTemplate[]>(`${pmsBase()}${PMS_ENDPOINTS.SETTINGS_ROLES}`, data),

  /** Replaces the role's permission set wholesale — see the service note. */
  updateRole: (roleKey: string, data: UpdateRoleTemplatePayload) =>
    apiService.put<PmsRoleTemplate[]>(
      `${pmsBase()}${PMS_ENDPOINTS.SETTINGS_ROLES}/${roleKey}`,
      data,
    ),

  /** Delete a custom role template; system + default roles are refused server-side. */
  deleteRole: (roleKey: string) =>
    apiService.delete<PmsRoleTemplate[]>(`${pmsBase()}${PMS_ENDPOINTS.SETTINGS_ROLES}/${roleKey}`),

  listUserRoles: () =>
    apiService.get<PmsUserRoleRow[]>(`${pmsBase()}${PMS_ENDPOINTS.SETTINGS_USER_ROLES}`),

  /** `role_key: null` clears the assignment back to the default template. */
  /**
   * Replace a member's overrides. The whole map is the intended final state —
   * `{}` clears them and hands the member back to their template.
   */
  updateUserOverrides: (membershipId: string, overrides: Record<string, boolean>) =>
    apiService.put<PmsUserRoleRow[]>(
      `${pmsBase()}${PMS_ENDPOINTS.SETTINGS_USER_OVERRIDES.replace(':membershipId', membershipId)}`,
      { overrides },
    ),

  assignUserRole: (membershipId: string, data: AssignUserRolePayload) =>
    apiService.put<PmsUserRoleRow[]>(
      `${pmsBase()}${PMS_ENDPOINTS.SETTINGS_USER_ROLES}/${membershipId}`,
      data,
    ),

  /**
   * Pause or resume a member's PMS ACCESS.
   *
   * App-scoped: this moves the Workspace-issued grant between ACTIVE and PAUSED
   * for PMS only. It is not a workspace deactivation and touches no other app.
   */
  setUserAccess: (membershipId: string, active: boolean) =>
    apiService.put<{ membership_id: string; access_status: string }>(
      `${pmsBase()}${PMS_ENDPOINTS.SETTINGS_USER_ACCESS.replace(':membershipId', membershipId)}`,
      { active },
    ),

  /**
   * Revoke the PMS grant — a soft delete, not a user deletion. The person, their
   * membership, their other apps and their PMS history all survive; re-granting
   * is done in Workspace.
   */
  revokeUserAccess: (membershipId: string) =>
    apiService.delete<{ membership_id: string; revoked: boolean }>(
      `${pmsBase()}${PMS_ENDPOINTS.SETTINGS_USER_ACCESS.replace(':membershipId', membershipId)}`,
    ),

  /**
   * Mark a member as an evaluator, or clear it.
   *
   * The FINAL state, not a toggle — two admins acting on the same person at
   * once would otherwise race to opposite results.
   */
  setEvaluatorFlag: (membershipId: string, isEvaluator: boolean) =>
    apiService.patch<PmsUserRoleRow[]>(
      `${pmsBase()}${PMS_ENDPOINTS.SETTINGS_USER_EVALUATOR.replace(':membershipId', membershipId)}`,
      { is_evaluator: isEvaluator },
    ),
};
